import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { ShieldCheck, FlaskConical, AlertTriangle, ArrowRight } from "lucide-react";

const ACK_KEY = "cg_compliance_ack_v1";

export const ComplianceGate = () => {
  const [open, setOpen] = useState(false);
  const [age, setAge] = useState(false);
  const [affirm, setAffirm] = useState(false);
  const [declined, setDeclined] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(ACK_KEY) !== "true") setOpen(true);
    } catch {
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const enter = () => {
    try { localStorage.setItem(ACK_KEY, "true"); } catch {}
    setOpen(false);
  };

  const canEnter = age && affirm;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="compliance-gate"
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/85 backdrop-blur-xl" />
          <motion.div
            className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-[#070a0f]/95 shadow-[0_0_80px_-10px_rgba(34,211,238,0.25)] overflow-hidden"
            initial={{ y: 40, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 26 }}
          >
            <div className="h-1 w-full bg-gradient-to-r from-cyan-400/0 via-cyan-400/80 to-fuchsia-500/60" />

            {!declined ? (
              <div className="p-7 sm:p-9">
                <div className="flex items-center gap-3 mb-5">
                  <div className="h-11 w-11 shrink-0 rounded-xl border border-cyan-400/20 bg-cyan-400/10 flex items-center justify-center">
                    <ShieldCheck className="h-5 w-5 text-cyan-300" />
                  </div>
                  <div>
                    <div className="font-serif text-2xl text-white leading-tight">Research Use Verification</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-400/80 mt-0.5">Access restricted · 21+</div>
                  </div>
                </div>

                <p className="text-sm text-slate-300 leading-relaxed">
                  All compounds and laboratory materials offered here are supplied strictly for qualified
                  <span className="text-white font-medium"> in-vitro and laboratory research</span>. Nothing on this
                  site is intended for human consumption or veterinary application.
                </p>

                <div className="mt-5 rounded-xl border border-amber-400/15 bg-amber-400/[0.06] p-3.5 flex gap-2.5">
                  <AlertTriangle className="h-4 w-4 text-amber-300 shrink-0 mt-0.5" />
                  <p className="text-[12px] text-amber-100/80 leading-relaxed">
                    These materials are not approved for diagnostic, therapeutic, or clinical use in or on any
                    living organism, including humans and animals.
                  </p>
                </div>

                <div className="mt-6 space-y-4">
                  <label className="flex items-start gap-3 cursor-pointer group" htmlFor="cg-age">
                    <Checkbox id="cg-age" checked={age} onCheckedChange={(v) => setAge(!!v)} data-testid="compliance-age-checkbox" className="mt-0.5 border-white/20 data-[state=checked]:bg-cyan-400 data-[state=checked]:border-cyan-400" />
                    <span className="text-[13px] text-slate-300 group-hover:text-slate-100 transition-colors">
                      I confirm that I am <span className="text-white font-medium">at least 21 years of age</span>.
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer group" htmlFor="cg-affirm">
                    <Checkbox id="cg-affirm" checked={affirm} onCheckedChange={(v) => setAffirm(!!v)} data-testid="compliance-research-checkbox" className="mt-0.5 border-white/20 data-[state=checked]:bg-cyan-400 data-[state=checked]:border-cyan-400" />
                    <span className="text-[13px] text-slate-300 group-hover:text-slate-100 transition-colors">
                      I confirm that I am an independent researcher or act on behalf of a qualified laboratory,
                      clinic, academic institution, or comparable research body. I acknowledge these materials
                      are intended solely for lawful in-vitro and laboratory study, must be handled only by
                      trained professionals, and are never to be used in or on humans or animals.
                    </span>
                  </label>
                </div>

                <div className="mt-7 flex flex-col-reverse sm:flex-row gap-3">
                  <Button
                    onClick={() => setDeclined(true)}
                    data-testid="compliance-exit-btn"
                    variant="ghost"
                    className="flex-1 rounded-xl border border-white/10 text-slate-400 hover:text-white hover:bg-white/5"
                  >
                    I do not qualify
                  </Button>
                  <Button
                    onClick={enter}
                    disabled={!canEnter}
                    data-testid="compliance-enter-btn"
                    className="flex-1 rounded-xl btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Enter Site <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-9 text-center" data-testid="compliance-restricted">
                <div className="mx-auto h-12 w-12 rounded-xl border border-red-400/20 bg-red-400/10 flex items-center justify-center mb-4">
                  <FlaskConical className="h-6 w-6 text-red-300" />
                </div>
                <div className="font-serif text-2xl text-white mb-2">Access Restricted</div>
                <p className="text-sm text-slate-400 leading-relaxed max-w-sm mx-auto">
                  This site is available only to verified researchers aged 21 or older who agree to the
                  in-vitro research terms. You may close this window.
                </p>
                <Button
                  onClick={() => setDeclined(false)}
                  data-testid="compliance-back-btn"
                  variant="ghost"
                  className="mt-6 rounded-xl border border-white/10 text-slate-300 hover:text-white hover:bg-white/5"
                >
                  Back to verification
                </Button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
